import { useEffect, useMemo, useState } from "react";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Loader2, Download, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { PageHeader } from "../components/common/PageHeader";
import { UploadLimitBanner } from "../components/common/UploadLimitBanner";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

const CAT_STYLE = {
  VALID_NEW: "bg-emerald-100 text-emerald-700",
  CROSS_PROJECT_DUPLICATE: "bg-amber-100 text-amber-700",
  INVALID_DATE: "bg-amber-100 text-amber-700",
  DUPLICATE_IN_FILE: "bg-red-100 text-red-700",
  DUPLICATE_EXISTING: "bg-red-100 text-red-700",
  INVALID_SERIAL: "bg-red-100 text-red-700",
  UNKNOWN_SUB_TYPE: "bg-red-100 text-red-700",
  UNKNOWN_MAKE: "bg-red-100 text-red-700",
  TYPE_MISMATCH: "bg-red-100 text-red-700",
  INACTIVE_SUB_TYPE: "bg-red-100 text-red-700",
  INACTIVE_MAKE: "bg-red-100 text-red-700",
  MISSING_REQUIRED_FIELD: "bg-red-100 text-red-700",
  OTHER_VALIDATION_ERROR: "bg-red-100 text-red-700",
};

// Sort order for the breakdown chips: errors first, then warnings, then valid.
const CAT_RANK = {
  INVALID_SERIAL: 0, DUPLICATE_IN_FILE: 0, DUPLICATE_EXISTING: 0, UNKNOWN_SUB_TYPE: 0,
  UNKNOWN_MAKE: 0, TYPE_MISMATCH: 0, INACTIVE_SUB_TYPE: 0, INACTIVE_MAKE: 0,
  MISSING_REQUIRED_FIELD: 0, OTHER_VALIDATION_ERROR: 0,
  CROSS_PROJECT_DUPLICATE: 1, INVALID_DATE: 1,
  VALID_NEW: 2,
};
const CAT_LABELS = {
  VALID_NEW: "Valid (new)",
  CROSS_PROJECT_DUPLICATE: "Cross-project duplicate",
  INVALID_DATE: "Invalid / unparsed date",
  DUPLICATE_IN_FILE: "Duplicate in file",
  DUPLICATE_EXISTING: "Already exists in project",
  INVALID_SERIAL: "Blank / invalid serial",
  UNKNOWN_SUB_TYPE: "Unknown material type",
  UNKNOWN_MAKE: "Unknown make",
  TYPE_MISMATCH: "Type mismatch",
  INACTIVE_SUB_TYPE: "Inactive material type",
  INACTIVE_MAKE: "Inactive make",
  MISSING_REQUIRED_FIELD: "Missing required field",
  OTHER_VALIDATION_ERROR: "Other validation error",
};

export default function MaterialUpload() {
  const { ctx, options, dataVersion } = useWorkspace();
  const [stores, setStores] = useState([]);
  const [types, setTypes] = useState([]);
  const [projectId, setProjectId] = useState(ctx.project?.id || "");
  const [storeId, setStoreId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [ackWarnings, setAckWarnings] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [catFilter, setCatFilter] = useState("__all__");

  useEffect(() => {
    api.get("/material-types", { params: { active_only: true, page_size: 500 } }).then((r) => setTypes(r.data.items || []));
  }, [dataVersion]);
  // Prefill from the global Active Project when a specific one is selected.
  useEffect(() => { setProjectId(ctx.project?.id || ""); setConfirmed(false); }, [ctx.project?.id]);
  useEffect(() => {
    if (projectId) api.get("/stores", { params: { project_id: projectId, page_size: 500 } }).then((r) => setStores(r.data.items || []));
    else setStores([]);
    setStoreId("");
  }, [projectId, dataVersion]);

  const projects = useMemo(() => options.projects || [], [options.projects]);
  const projName = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);
  const typeName = useMemo(() => types.find((t) => t.id === typeId), [types, typeId]);

  const changeProject = (v) => { setProjectId(v); setConfirmed(false); setPreview(null); };
  const changeType = (v) => { setTypeId(v); setConfirmed(false); setPreview(null); };

  const downloadTemplate = async () => {
    setDlBusy(true);
    try {
      const res = await api.get("/materials/upload/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "material_upload_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Template download failed"); }
    finally { setDlBusy(false); }
  };

  const doPreview = async () => {
    if (!projectId || !typeId || !file) { toast.error("Confirm project + type and choose a file"); return; }
    setLoading(true); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", projectId);
      fd.append("material_type_id", typeId);
      if (storeId) fd.append("store_id", storeId);
      const { data } = await api.post("/materials/upload/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setAckWarnings(false);
      if (data.already_applied) toast.info("This exact file was already applied for this project & type");
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setLoading(false); }
  };

  const doApply = async () => {
    if (!preview?.batch_id) return;
    setApplying(true);
    try {
      const { data } = await api.post("/materials/upload/apply", { batch_id: preview.batch_id });
      toast.success(`Applied: ${data.inserted} created, ${data.skipped_duplicates} skipped`);
      setPreview((p) => ({ ...p, already_applied: true, result: data }));
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setApplying(false); }
  };

  const reset = () => { setFile(null); setPreview(null); setCatFilter("__all__"); setAckWarnings(false); };

  const downloadReport = async () => {
    if (!preview?.batch_id) return;
    try {
      const res = await api.get(`/materials/upload/${preview.batch_id}/report`, {
        params: catFilter !== "__all__" ? { category: catFilter } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `material_validation_${preview.batch_id.slice(0, 8)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Report download failed"); }
  };

  const rows = useMemo(() => {
    if (!preview?.rows) return [];
    return catFilter === "__all__" ? preview.rows : preview.rows.filter((r) => r.category === catFilter);
  }, [preview, catFilter]);

  const s = preview?.summary || {};
  const mixed = (s.type_mismatch || 0) > 0;
  const hasWarnings = (s.warning || 0) > 0;
  const canApply = preview && !preview.already_applied && !mixed && (s.applicable || 0) > 0 && (!hasWarnings || ackWarnings);

  return (
    <div>
      <PageHeader title="Material Upload" subtitle="Confirm Project + Type → Download template → Upload → Preview → Apply" />
      <UploadLimitBanner kind="material" />

      {/* STEP 1 — Confirm Project + Material Type context */}
      {!confirmed ? (
        <div className="max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="upload-context-step">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700"><ShieldCheck className="h-4 w-4 text-blue-600" /> Confirm Upload Context</div>
          <p className="mb-4 text-xs text-slate-500">Material Upload creates serial-level records. Confirm the correct Project &amp; Material Type before choosing a file.</p>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Project *</Label>
              <SearchableSelect testid="upload-project-select" value={projectId} onChange={changeProject} options={projects.map((p) => ({ value: p.id, label: p.project_code ? `${p.project_code} — ${p.project_name}` : p.project_name, sub: p.client || p.location }))} placeholder="Select project" className="mt-1" />
            </div>
            <div>
              <Label>Material Type *</Label>
              <SearchableSelect testid="upload-type-select" value={typeId} onChange={changeType} options={types.map((t) => ({ value: t.id, label: t.type_name }))} placeholder="Select material type" className="mt-1" />
            </div>
            <div>
              <Label>Store (optional)</Label>
              <SearchableSelect testid="upload-store-select" value={storeId} onChange={setStoreId} disabled={!projectId} options={stores.map((st) => ({ value: st.id, label: st.store_name }))} placeholder="Optional planned store" className="mt-1" />
            </div>
          </div>

          {projectId && typeId && (
            <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="upload-context-summary">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Please confirm</div>
              <div className="mt-2 grid gap-1 text-sm text-slate-700">
                <div><span className="text-slate-400">Project:</span> <span className="font-semibold">{projName?.project_name}</span> {projName?.project_code && <span className="font-mono text-xs text-slate-500">({projName.project_code})</span>}</div>
                <div><span className="text-slate-400">Material Type:</span> <span className="font-semibold">{typeName?.type_name}</span></div>
                {storeId && <div><span className="text-slate-400">Store:</span> {stores.find((x) => x.id === storeId)?.store_name}</div>}
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-end">
            <Button data-testid="upload-confirm-context-btn" disabled={!projectId || !typeId} onClick={() => setConfirmed(true)} className="bg-blue-600 hover:bg-blue-700">
              Confirm &amp; Continue <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Confirmed context bar */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5" data-testid="upload-confirmed-bar">
            <div className="text-sm text-slate-700">
              <span className="text-slate-400">Project:</span> <span className="font-semibold text-blue-800">{projName?.project_name}</span>
              <span className="mx-2 text-slate-300">|</span>
              <span className="text-slate-400">Type:</span> <span className="font-semibold text-blue-800">{typeName?.type_name}</span>
              {storeId && <><span className="mx-2 text-slate-300">|</span><span className="text-slate-400">Store:</span> {stores.find((x) => x.id === storeId)?.store_name}</>}
            </div>
            <Button variant="outline" size="sm" data-testid="upload-back-context-btn" onClick={() => { setConfirmed(false); reset(); }}><ArrowLeft className="mr-1.5 h-4 w-4" /> Change Project / Type</Button>
          </div>

          {/* File + template */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-medium text-slate-600">Upload the filled Material Upload Template (.xlsx)</span>
              <Button variant="outline" size="sm" data-testid="upload-template-download" onClick={downloadTemplate} disabled={dlBusy}>
                {dlBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Download Template
              </Button>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3 hover:bg-slate-50" data-testid="upload-file-label">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-slate-600">{file ? file.name : "Choose .xlsx file"}</span>
                <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="upload-file-input" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} />
              </label>
              <Button data-testid="upload-preview-btn" onClick={doPreview} disabled={loading || !file} className="bg-slate-800 hover:bg-slate-900">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UploadCloud className="mr-1.5 h-4 w-4" /> Preview</>}
              </Button>
            </div>
          </div>

          {preview && (
            <div className="mt-6 space-y-4">
              {mixed && (
                <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="upload-mixed-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Mixed / mismatched Material Types detected ({s.type_mismatch} row(s)). The selected Type does not match the file. Use one Primary Material Type per upload file.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="upload-summary">
                <SummaryCard label="Total" value={s.total} tone="slate" />
                <SummaryCard label="Valid" value={s.valid} tone="green" icon={CheckCircle2} />
                <SummaryCard label="Warnings" value={s.warning} tone="amber" icon={AlertTriangle} />
                <SummaryCard label="Errors" value={s.error} tone="red" icon={XCircle} />
                <SummaryCard label="Applicable" value={s.applicable} tone="blue" />
              </div>

              {Object.keys(preview.categories || {}).length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="upload-cat-breakdown">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Breakdown by result</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(preview.categories)
                      .sort((a, b) => (CAT_RANK[a[0]] ?? 3) - (CAT_RANK[b[0]] ?? 3) || b[1] - a[1])
                      .map(([cat, count]) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setCatFilter((prev) => (prev === cat ? "__all__" : cat))}
                          data-testid={`upload-cat-chip-${cat}`}
                          className={`rounded-full border px-3 py-1 text-xs transition ${CAT_STYLE[cat] || "bg-slate-100 text-slate-600"} ${catFilter === cat ? "ring-2 ring-offset-1 ring-slate-400" : "hover:opacity-90"}`}
                          title={`${count} × ${CAT_LABELS[cat] || cat} — click to filter the rows below`}
                        >
                          <span className="font-bold">{count}</span> × {CAT_LABELS[cat] || cat}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Select value={catFilter} onValueChange={setCatFilter}>
                  <SelectTrigger data-testid="upload-cat-filter" className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All categories</SelectItem>
                    {Object.keys(preview.categories || {}).map((c) => <SelectItem key={c} value={c}>{c} ({preview.categories[c]})</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={downloadReport} data-testid="upload-download-report">
                    <Download className="mr-1.5 h-4 w-4" /> Download Report
                  </Button>
                  {preview.already_applied ? (
                    <Badge className="bg-emerald-100 px-3 py-1.5 text-emerald-700">Applied · {preview.result?.inserted ?? 0} created</Badge>
                  ) : (
                    <>
                      {hasWarnings && (
                        <label className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="upload-ack-warnings-label">
                          <Checkbox checked={ackWarnings} onCheckedChange={(v) => setAckWarnings(!!v)} data-testid="upload-ack-warnings" />
                          I reviewed the {s.warning} warning row(s) and want to upload anyway
                        </label>
                      )}
                      <Button variant="outline" onClick={reset} data-testid="upload-reset-btn">Reset</Button>
                      <Button data-testid="upload-apply-btn" onClick={doApply} disabled={!canApply || applying} className="bg-blue-600 hover:bg-blue-700">
                        {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : `Apply ${s.applicable || 0} rows`}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100/80">
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Row</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Serial</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Material Type</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Make</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Result</th>
                    </tr>
                  </thead>
                  <tbody data-testid="upload-rows">
                    {rows.slice(0, 500).map((r) => (
                      <tr key={r.row_no} className="border-b border-slate-100">
                        <td className="px-4 py-2 text-sm text-slate-500">{r.row_no}</td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.serial || <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{r.material_type}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{r.make}</td>
                        <td className="px-4 py-2">
                          <Badge className={`${CAT_STYLE[r.category] || "bg-slate-100 text-slate-600"} hover:opacity-100`}>{r.category}</Badge>
                          {r.message && <span className="ml-2 text-xs text-slate-400">{r.message}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone, icon: Icon }) {
  const tones = { slate: "text-slate-700", green: "text-emerald-600", amber: "text-amber-600", red: "text-red-600", blue: "text-blue-600" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${tones[tone]}`}>{value ?? 0}</div>
    </div>
  );
}
